/**
 * Base section IDs used across the address page.
 * These are the unprefixed IDs that get prefixed with viewport.
 * 
 * Example: SECTION_IDS.OVERVIEW ('overview') becomes 'desktop-overview' or 'mobile-overview'
 */
export const SECTION_IDS = {
  OVERVIEW: 'overview',
  PRICE_HISTORY: 'price-history',
  HOME_APPRAISAL: 'home-appraisal',
  MAP: 'map',
  COMMUTE: 'commute',
  SIMILAR_LISTINGS: 'similar-listings',
} as const;

/**
 * Type for section ID values
 */
export type SectionId = typeof SECTION_IDS[keyof typeof SECTION_IDS];

/**
 * Generate viewport-prefixed section ID for scroll targeting.
 * 
 * Desktop and mobile layouts render simultaneously, so sections need unique IDs
 * to prevent HTML validation errors from duplicate IDs.
 * 
 * @param baseId - The base section ID without viewport prefix (use SECTION_IDS constants)
 * @param viewport - The viewport identifier ('desktop' or 'mobile')
 * @returns Prefixed section ID (e.g., 'desktop-overview', 'mobile-similar-listings')
 * 
 * @example
 * import { SECTION_IDS, getSectionId } from './get-section-id';
 * getSectionId(SECTION_IDS.OVERVIEW, 'desktop') // → 'desktop-overview'
 * getSectionId(SECTION_IDS.SIMILAR_LISTINGS, 'mobile') // → 'mobile-similar-listings'
 */
export function getSectionId(baseId: SectionId, viewport: 'desktop' | 'mobile'): string {
  return `${viewport}-${baseId}`;
}
